import { StoreCode } from '@portal/schema/storeCode';
import type { CarouselMedia, Variant } from '@portal/state/store/types';
import { getFormattedShopifyId } from 'shared/utils/shopify/ids';
import type { BaseImage, BaseVideoWithPlayer, SanityKeyed } from 'types/studio/studio';
import type { ShopifyCartItem } from '../BuildBundleV3/types';
import { isPouchOrTravelPackV53Variant } from '@portal/shared/utils/shopify/isPouchOrTravelPackV53Variant';
import { ensureStoreScope } from './debug/ensureStoreScope';
export const flavorsMapper = {
  original: {
    title: 'Original',
    color: '#105C5C'
  },
  tropical: {
    title: 'Tropical',
    color: '#E3BE38'
  }
};

/**
 * Generates a unique identifier for a product variant by combining the first subscription variant ID
 * with the variant's quantity.
 *
 * @param variant - The product variant object containing subscription variant IDs and quantity
 * @returns A string in the format `{variantId}-{quantity}` or an empty string if no variant ID is found
 */

// types: Variant matches your shape in the dump
type GetUidOpts = {
  isOtp?: boolean; // which lane are we in?
  store?: string; // filter the id items by targetStore
  includeQtySuffix?: boolean; // default true: `${id}-${quantity}`
};

export const getUniqueVariantId = (
  variant: Variant,
  flavor?: string | null,
  opts: GetUidOpts = {}
) => {
  const { isOtp = false, store, includeQtySuffix = true } = opts;
  // pick the right list by lane
  const list = (isOtp ? variant.oneTimeVariantId : variant.subscriptionVariantIds) ?? [];
  if (!list.length) return '';

  // filter by store if given
  const byStore = store ? list.filter((i) => i.targetStore === store) : list;

  // prefer a flavor match (ignore case), otherwise fall back to "original",
  // otherwise the first available
  const wantedFlavor = (flavor ?? '').toLowerCase();
  const pick =
    (wantedFlavor &&
      byStore.find((i) => (i.flavorQueryParam ?? '').toLowerCase() === wantedFlavor)) ||
    byStore.find((i) => (i.flavorQueryParam ?? '').toLowerCase() === 'original') ||
    byStore[0];

  const baseId = pick?.variantId;
  if (!baseId) return '';

  return includeQtySuffix ? `${baseId}-${variant.quantity}` : baseId;
};

export function getSelectedVariant(
  variants: Variant[] | null | undefined,
  selectedVariantId: string,
  store: string,
  flavor?: string | null
): Variant | undefined {
  if (!variants?.length || !selectedVariantId) return undefined;

  // only keep variants valid for this store (same as before)
  const scoped = getVariantsPerStore(variants, store);

  // exact match against IDs generated for BOTH lanes
  return scoped.find((variant) => {
    const subId = getUniqueVariantId(variant, flavor ?? undefined, { isOtp: false, store });
    const otpId = getUniqueVariantId(variant, flavor ?? undefined, { isOtp: true, store });
    return selectedVariantId === subId || selectedVariantId === otpId;
  });
}

export function getVariantsPerStore(
  variants: Variant[] | null | undefined,
  store: string
): Variant[] {
  if (!variants) return [];
  return variants
    .map((variant) => ({
      ...variant,
      subscriptionVariantIds: variant.subscriptionVariantIds?.filter(
        (item) => item.targetStore === store
      ),
      oneTimeVariantId: variant?.oneTimeVariantId?.filter((item) => item.targetStore === store)
    }))
    .filter(
      (variant) =>
        variant.subscriptionVariantIds?.length > 0 ||
        (variant.oneTimeVariantId && variant.oneTimeVariantId.length > 0)
    );
}

export function addPdpWelcomeKit(
  variant: Variant,
  storeCode: StoreCode,
  isOtp: boolean
): ShopifyCartItem[] {
  if (!variant.welcomeKitIds) return [];

  const targetWelcomeKit = variant.welcomeKitIds.find(
    ({ targetStore, isOTPKit }) => targetStore === storeCode && isOtp === !!isOTPKit
  );

  return targetWelcomeKit
    ? [
        {
          merchandiseId: getFormattedShopifyId('ProductVariant', targetWelcomeKit.welcomeKitId),
          quantity: 1,
          attributes: [
            {
              // double underscore makes the custom attribute hidden on the checkout
              key: isOtp ? '__custom.otp_welcome_kit' : '__custom.welcome_kit',
              value: 'true'
            }
          ]
        } as ShopifyCartItem
      ]
    : [];
}

type VariantIdLike = {
  variantId: string;
  targetStore: string;
  flavorQueryParam?: string | null;
  sellingPlanId?: string;
  _key: string;
};

const TRACE = false;

export const getSubscriptionLineItems = (
  selectedVariant: Variant,
  quantity?: number,
  productCadence?: string,
  isOtpProduct?: boolean,
  flavor?: string | null,
  store?: StoreCode,
  isLoggedIn?: boolean
) => {
  if (!selectedVariant) return [];

  const sellingPlanToSubmit = isOtpProduct
    ? 'otp'
    : productCadence || selectedVariant?.subscriptionVariantIds?.[0].sellingPlanId;
  // Scope the source arrays BEFORE any other logic
  ensureStoreScope(selectedVariant?.subscriptionVariantIds, store, 'getSubscriptionLineItems.sub');
  ensureStoreScope(selectedVariant?.oneTimeVariantId, store, 'getSubscriptionLineItems.otp');

  // OTP special-case for logged-in pouch/pack
  const primaryVariantId =
    selectedVariant?.subscriptionVariantIds?.[0]?.variantId ||
    selectedVariant?.oneTimeVariantId?.[0]?.variantId ||
    '';

  const isPouchOrPack = store ? isPouchOrTravelPackV53Variant(primaryVariantId, store) : false;

  const shouldUseSubscriptionVariantForOtp = Boolean(isLoggedIn && isOtpProduct && isPouchOrPack);

  const rawItems =
    sellingPlanToSubmit === 'otp'
      ? shouldUseSubscriptionVariantForOtp
        ? selectedVariant.subscriptionVariantIds
        : selectedVariant.oneTimeVariantId
      : selectedVariant.subscriptionVariantIds;

  if (!rawItems?.length) return [];

  // normalize helpers
  const normalize = (s?: string | null) => (s ?? '').trim().toLowerCase();
  const requestedFlavorRaw = (flavor ?? '').trim();
  const requestedFlavor = normalize(flavor);

  // -------- 1) FILTER BY STORE FIRST --------
  let items: VariantIdLike[] = (rawItems as unknown as VariantIdLike[]).filter(
    (i) => !store || i.targetStore === store
  );

  // If nothing matches current store (shouldn't happen), fall back to all to avoid hard empty.
  if (!items.length) items = rawItems as unknown as VariantIdLike[];

  // -------- 2) THEN FILTER BY FLAVOR (with fallback to default lane) --------
  const availableFlavors = new Set(items.map((i) => normalize(i.flavorQueryParam)));
  const hasDefaultLane = items.some((i) => !i.flavorQueryParam);
  const isFallbackToDefaultLane =
    !!requestedFlavor && !availableFlavors.has(requestedFlavor) && hasDefaultLane;

  let itemsByFlavor = items;

  if (requestedFlavor) {
    if (availableFlavors.has(requestedFlavor)) {
      // Requested flavor exists here; filter to it
      itemsByFlavor = items.filter(
        (i) => (i.flavorQueryParam ?? '').trim().toLowerCase() === requestedFlavor
      );
    } else {
      // Requested flavor is NOT available on this PDP (e.g., Omega)
      itemsByFlavor = hasDefaultLane ? items.filter((i) => !i.flavorQueryParam) : items;
    }
  } else {
    // No specific flavor requested; prefer default lane if available
    itemsByFlavor = hasDefaultLane ? items.filter((i) => !i.flavorQueryParam) : items;
  }

  if (TRACE) {
    console.log('[TRACE getSubscriptionLineItems]', {
      lane: sellingPlanToSubmit,
      store,
      isOtpProduct,
      isLoggedIn,
      shouldUseSubscriptionVariantForOtp,
      requestedFlavor,
      counts: { raw: rawItems.length, store: items.length, flavor: itemsByFlavor.length },
      ids: itemsByFlavor.map((i) => i.variantId)
    });
  }

  const isSubscription =
    !!sellingPlanToSubmit &&
    Number.isFinite(Number(sellingPlanToSubmit)) &&
    Number(sellingPlanToSubmit) > 0;

  return itemsByFlavor.map((item) => {
    // If the lane has a flavor, use it; else, if we fell back, write the requested flavor.
    const flavorToWrite =
      item.flavorQueryParam && item.flavorQueryParam.trim()
        ? item.flavorQueryParam
        : isFallbackToDefaultLane
        ? requestedFlavorRaw
        : '';

    const attributes = [
      ...(isSubscription ? [{ key: '__shop_subscriptions', value: 'true' } as const] : []),
      ...(flavorToWrite ? [{ key: '__flavor', value: flavorToWrite }] : [])
    ];

    return {
      merchandiseId: getFormattedShopifyId('ProductVariant', item.variantId),
      quantity: (quantity || selectedVariant?.quantity) ?? 1,
      ...(isSubscription
        ? { sellingPlanId: getFormattedShopifyId('SellingPlan', String(sellingPlanToSubmit)) }
        : {}),
      attributes
    } as ShopifyCartItem;
  });
};

export const formatPdpCarouselMedia = (
  carouselMedia?: Array<SanityKeyed<CarouselMedia>>
): SanityKeyed<BaseImage | BaseVideoWithPlayer>[] | undefined =>
  carouselMedia?.map(
    (media) =>
      ({
        ...media,
        _type: media._type === 'image' ? 'baseImage' : 'baseVideoWithPlayer',
        fit: media.fit === 'adaptive' ? 'cover' : media.fit
      }) as SanityKeyed<BaseImage | BaseVideoWithPlayer>
  );
